<template>
  <component v-bind="elementProps"
             :is="elementTag"
             class="rui-menu-item">
    <rui-icon v-if="icon" :name="icon" :title="caption" />
    <span v-if="$slots.default || caption">
      <!-- @slot Item content. Default to `caption` if not provided -->
      <slot>{{ caption }}</slot>
    </span>
  </component>
</template>

<script>
import RuiIcon from '../Icon/Icon.vue'

const variants = ['destructive', 'creative', 'recorder', 'canceling', 'helper']

/**
 * Item to use in `rui-menu`
 *
 * @preset Default { "props": { "caption": "Entry #1", "href": "#" } }
 *
 * @propsGroup Display [variant]
 * @propsGroup State   [active]
 * @propsGroup Content [caption, icon, to, href]
 */
export default {
  name: 'RuiMenuItem',
  components: { RuiIcon },
  props: {
    /** Main content if not using the slot. Is also used for component `title` attribute */
    caption: { required: false, type: String, default: null },
    /** Additional icon */
    icon: { required: false, type: String, default: null },
    /** Router-link "to" prop. Use either `to` or `link`, not both */
    to: { required: false, type: [Object, String], default: null },
    /** Link. Use either `link` or `to`, not both */
    href: { required: false, type: String, default: null },
    /** Color variant */
    variant: { required: false, type: String, default: null, validator: v => variants.includes(v) },
    /** Active state */
    active: { required: false, type: Boolean, default: false },
  },
  computed: {
    elementTag () {
      if (this.to) return 'RouterLink'
      if (this.href) return 'a'

      return 'button'
    },
    elementProps () {
      const props = {
        class: [
          this.variant && `rui-menu-item--${this.variant}`,
          this.active && 'rui-menu-item--active',
        ],
        title: this.caption,
      }

      if (this.to) {
        props.to = this.to
      } else if (this.href) {
        props.href = this.href
      } else {
        props.role = 'button'
      }

      return props
    },
  },
}
</script>
